import { requestFormById } from 'api/operations/formService';
import { runInAction, makeObservable, action, observable, computed } from 'mobx';
import { addFullEmployeeToGroup } from 'api/operations/electionService';
import { toastManager } from '@insurancemenu/imenu-components-lib/dist/components/Toast/toastManager';
import { ToastColorTypes } from '@insurancemenu/imenu-components-lib/dist/types/Toast';
import { DynamicFormDelegate } from '@insurancemenu/imenu-components-lib/dist/utils/forms';
import { FormObjectFromJSON } from '@insurancemenu/imenu-components-lib/dist/types/dynamicForms';
import { getRootStoreInstance } from 'store/rootStore';
import FullFormPageStore, { FullEmployeeFormDomainParams } from './pageStore';

export default class EmployeeProfileTab {
  private _rootStore = getRootStoreInstance();
  private _domainData: FullEmployeeFormDomainParams;
  private _pageStore: FullFormPageStore;
  private _isLoading: boolean = false;
  private _errors: string[] = [];

  public dynamicJSONFormDelegate = runInAction(
    () =>
      new DynamicFormDelegate({
        currentUserRoles: this._rootStore.userStore.userRoles,
        fetchForm: async (formId: string): Promise<FormObjectFromJSON> => {
          return requestFormById(formId);
        },
        onSubmit: this.submitForm.bind(this),
        disableResetAfterSubmit: true,
      }),
  );

  public get isValid(): boolean {
    if (this._isLoading) return true;
    return this.dynamicJSONFormDelegate.formController === null ? true : this.dynamicJSONFormDelegate.isFormValid;
  }

  public get isLoading(): boolean {
    return this._pageStore.isInitialDataLoading || this._isLoading;
  }

  public get errors(): string[] {
    return this._errors;
  }

  private _setErrors(errors: string[]): void {
    this._errors = errors;
  }

  private _setIsLoading(isLoading: boolean): void {
    this._isLoading = isLoading;
  }

  private async _getCreationForm(formId: string): Promise<void> {
    await runInAction(async () => {
      await this.dynamicJSONFormDelegate.buildForm(formId);
      this._setErrors(['Complete the employee profile with required information to proceed.']);
    });
  }

  private async _getEditionForm(formId: string): Promise<void> {
    if (!this._pageStore.subscriberExists) {
      throw new Error('Attempted to get edition form without an existing subscriber!');
    }

    await this.dynamicJSONFormDelegate.buildForm(
      formId,
      this._pageStore.electionsModule.subscribers[0]?.subscriberDelegate?.subscriber.common,
    );
  }

  public async fetchForm(): Promise<void> {
    this._setIsLoading(true);

    const quotingSubFormId = this._pageStore.electionsModule.requiredSubInfo?.quotingSubFormId;
    if (quotingSubFormId) {
      await this.dynamicJSONFormDelegate.buildForm(quotingSubFormId);
      await runInAction(async () => {
        if (this._pageStore.electionsModule.subscribersExist) {
          await this._getEditionForm(quotingSubFormId);
        } else {
          await this._getCreationForm(quotingSubFormId);
        }
        this._setIsLoading(false);
      });
    }

    runInAction(() => {
      this._setIsLoading(false);
    });
  }

  public async submitForm(): Promise<void> {
    this._setErrors([]);
    if (this._pageStore.subscriberExists) {
      await this._updateSubscriber();
    } else {
      await this._createSubscriber();
    }
  }

  private async _createSubscriber(): Promise<void> {
    if (!this.dynamicJSONFormDelegate.isFormValid) return;
    this._setIsLoading(true);

    const newSubscriberData = this.dynamicJSONFormDelegate.formAnswers;

    const employeeId = await addFullEmployeeToGroup(this._domainData.groupId, newSubscriberData);
    await runInAction(async () => {
      // Create the subscriber module to unblock next tabs.
      await this._pageStore.setupPageForNewEmployee(employeeId);
      runInAction(() => {
        toastManager.addToast('Employee created successfully', ToastColorTypes.SUCCESS);
        this._setErrors([]);
        this._setIsLoading(false);
      });
    });
  }

  private async _updateSubscriber(): Promise<void> {
    if (!this.dynamicJSONFormDelegate.isFormValid) return;
    this._setIsLoading(true);
    const updatedSubscriberData = this.dynamicJSONFormDelegate.formAnswers;
    await this._pageStore.electionsModule?.subscribers[0]?.subscriberDelegate?.updatePrimary(
      updatedSubscriberData,
      this._domainData.periodId,
    );
    runInAction(() => {
      toastManager.addToast('Employee updated successfully', ToastColorTypes.SUCCESS);
      this._setIsLoading(false);
    });
  }

  constructor(readonly domainData: FullEmployeeFormDomainParams, readonly pageStore: FullFormPageStore) {
    this._domainData = domainData;
    this._pageStore = pageStore;

    makeObservable<
      EmployeeProfileTab,
      | '_domainData'
      | '_pageStore'
      | '_createSubscriber'
      | '_updateSubscriber'
      | '_getCreationForm'
      | '_getEditionForm'
      | '_isLoading'
      | '_setIsLoading'
      | '_errors'
      | '_setErrors'
    >(this, {
      _domainData: observable,
      _pageStore: observable,
      _isLoading: observable,
      _errors: observable,
      dynamicJSONFormDelegate: observable,
      errors: computed,
      isLoading: computed,
      isValid: computed,
      _setErrors: action.bound,
      _setIsLoading: action.bound,
      _getCreationForm: action.bound,
      _getEditionForm: action.bound,
      _createSubscriber: action.bound,
      _updateSubscriber: action.bound,
      submitForm: action.bound,
      fetchForm: action.bound,
    });
  }
}
